import React from 'react';
import { Box, Typography } from '@mui/material';
import { normalizeUnifiedSourceResearchPublic, type BtbPlanningBaselinePublic, type DavisBaconScheduleSummary, type EstimateJob, type EstimateLoadData, type EstimateRevision, type UnifiedSourceCheckedPublic, type UnifiedSourceLaneProgress, type UnifiedSourceLaneState, type UnifiedSourceResearchPublic } from '../api/constructionPriceEstimatorFunctions';
import { normalizeApproximateCostPlan, type ApproximateCostPlanPublic } from '../api/approximateCostPlan';
import { projectSourceLedger, projectUnifiedResearch } from './ConstructionPricePublicProjection';

export const unavailableApproximateCostPlan = (
  result: UnifiedSourceResearchPublic | null | undefined,
  revision: EstimateRevision | null,
): ApproximateCostPlanPublic => {
  // The browser may show award totals as context, but a numerical award range
  // is reserved for the server-selected qualified cohort.
  const range = result?.recommendation.outcome === 'recommended' ? result.recommendation.range : null;
  const point = result?.recommendation.outcome === 'recommended' ? result.recommendation.award_point : null;
  const revisionTotals = revision?.totals ?? {};
  const amount = point?.amount ?? range?.median ?? revisionTotals.base ?? null;
  const low = range?.low ?? revisionTotals.low ?? null;
  const high = range?.high ?? revisionTotals.high ?? null;
  const primarySource = result?.recommendation.primary_source_label;
  const awardAvailable = primarySource === 'Previous federal awards' || revision?.mode === 'historical_comparable_rom' || revision?.mode === 'comparable_only_rom';
  const numeric = (value: string | number | null | undefined): string | null => {
    if (typeof value === 'number') return Number.isFinite(value) ? String(value) : null;
    return typeof value === 'string' && /^-?\d{1,15}(?:\.\d{1,10})?$/.test(value) ? value : null;
  };
  return {
    scope_label: 'No source-backed work-item breakdown is available for this scope.',
    status_label: 'No source-backed cost breakdown is available for this scope.',
    installed_quantity_label: '',
    purchased_quantity_label: '',
    assumptions: [],
    exclusions: [],
    labor_total: null,
    materials_total: null,
    labor_lines: [],
    material_lines: [],
    other_costs: [{ label: 'Other costs', amount: null, basis: 'No source-backed component estimate is available.' }],
    priced_subtotal: null,
    award_reference: awardAvailable ? (point ? {
      reference_kind: 'conservative_comparable_award_point' as const,
      amount: numeric(point.amount), compatible: true,
      basis_label: point.basis_label, source_label: point.source_label,
      selection_rule: point.selection_rule, geography_tier: point.geography_tier,
      qualified_family_count: point.qualified_family_count,
      source_date_label: point.source_date_label, policy_version: point.policy_version,
    } : {
      amount: numeric(amount), low: numeric(low), high: numeric(high),
      basis_label: result?.recommendation.basis_label ?? 'Previous federal awards',
      // A legacy saved ROM is compatible only without a current contrary
      // recommendation; context-only and insufficient outcomes stay context.
      compatible: result?.recommendation.outcome === 'recommended'
        || (!result && (revision?.mode === 'historical_comparable_rom'
          || revision?.mode === 'comparable_only_rom')),
    }) : null,
    warnings: result?.recommendation.range_quality_warning ? [result.recommendation.range_quality_warning] : [],
  };
};

/**
 * The recommendation already contains the server-selected totals.  When its
 * cohort deliberately widens from county to state, carry those exact values
 * into the one ROM table and make the geographic limitation explicit.  The
 * browser never selects, ranks, narrows, or calculates award values.
 */
export const statewideAwardReferencePlan = (
  plan: ApproximateCostPlanPublic,
  result: UnifiedSourceResearchPublic | null | undefined,
): ApproximateCostPlanPublic => {
  const recommendation = result?.recommendation;
  const point = recommendation?.award_point;
  const cohort = result?.comparable_cohort;
  const range = recommendation?.range;
  const amount = (value: string | number | null | undefined): string | null => {
    if (typeof value === 'number') return Number.isFinite(value) && value >= 0 ? String(value) : null;
    return typeof value === 'string' && /^\d{1,15}(?:\.\d{1,10})?$/.test(value) ? value : null;
  };
  if (
    recommendation?.outcome !== 'recommended'
    || recommendation.primary_source_label !== 'Previous federal awards'
  ) return plan;
  if (point) {
    const pointAmount = amount(point.amount);
    if (!pointAmount) return plan;
    return {
      ...plan,
      award_reference: {
        reference_kind: 'conservative_comparable_award_point',
        amount: pointAmount, compatible: true,
        basis_label: point.basis_label, source_label: point.source_label,
        selection_rule: point.selection_rule, geography_tier: point.geography_tier,
        qualified_family_count: point.qualified_family_count,
        source_date_label: point.source_date_label, policy_version: point.policy_version,
      },
    };
  }
  if (!range) return plan;
  const low = amount(range.low);
  const median = amount(range.median);
  const high = amount(range.high);
  if (!low || !median || !high) return plan;
  return {
    ...plan,
    award_reference: {
      amount: median,
      low,
      high,
      compatible: true,
      basis_label: cohort?.status === 'broader_supported' && cohort.geographic_tier === 'state'
        ? 'Statewide federal award reference; locality is not county-specific.'
        : 'Historical USAspending award totals.',
    },
  };
};

export const UnifiedResearchProgress: React.FC<{ lanes: UnifiedSourceLaneProgress[] }> = ({ lanes }) => (
  <Box component="ul" sx={{ m: 0, mt: 1, pl: 2.5 }} aria-label="Price source progress">
    {lanes.map((lane, index) => <li key={`${lane.label}-${index}`}>
      <Typography variant="body2">{lane.label}{lane.message ? ` — ${lane.message}` : ''}</Typography>
    </li>)}
  </Box>
);

export const btbPlanningBaselineForDisplay = (value: unknown): BtbPlanningBaselinePublic | null => {
  if (!value || typeof value !== 'object' || Array.isArray(value)) return null;
  const baseline = value as Partial<BtbPlanningBaselinePublic> & Record<string, unknown>;
  const price = baseline.value;
  const validAmount = (typeof price?.amount === 'number' && Number.isFinite(price.amount) && price.amount >= 0)
    || (typeof price?.amount === 'string' && /^\d{1,8}(?:\.\d{1,2})?$/.test(price.amount.trim()));
  const limitation = baseline.limitations;
  if (baseline.source_label !== 'DOE/NREL Buildings Technology Baseline (2024)'
    || baseline.evidence_type !== 'National planning baseline'
    || baseline.geography_label !== 'National'
    || (baseline.building_segment !== 'commercial' && baseline.building_segment !== 'residential')
    || (baseline.cost_basis_label !== 'Installed' && baseline.cost_basis_label !== 'Retail')
    || price?.currency !== 'USD' || !validAmount || typeof price.unit_label !== 'string' || !price.unit_label.trim()
    || typeof baseline.technology_label !== 'string' || !baseline.technology_label.trim()
    || typeof baseline.value_year_label !== 'string' || !baseline.value_year_label.trim()
    || typeof baseline.attribution !== 'string' || !baseline.attribution.trim()
    || !Array.isArray(limitation) || limitation.length !== 1
    || limitation[0] !== 'Planning baseline for a supported building-energy technology; not a project-specific quote or complete construction estimate.') return null;
  return baseline as BtbPlanningBaselinePublic;
};

export type SourceRun = Record<string, unknown>;

export const sourceRunRecords = (value: unknown): SourceRun[] => Array.isArray(value)
  ? value.filter((item): item is SourceRun => Boolean(item && typeof item === 'object'))
  : [];

export const permittedUSAspendingUrl = (candidate: unknown): string | null => {
  if (typeof candidate !== 'string') return null;
  try {
    const url = new URL(candidate);
    const permittedHost = url.hostname === 'usaspending.gov' || url.hostname.endsWith('.usaspending.gov');
    return url.protocol === 'https:' && permittedHost ? url.toString() : null;
  } catch {
    return null;
  }
};

export const usaspendingSourceRun = (sourceRuns: SourceRun[]): SourceRun | undefined => (
  [...sourceRuns].reverse().find((run) => (
    typeof run.source === 'string' && run.source.toLowerCase().startsWith('usaspending')
  ))
);

/**
 * Source runs contain private diagnostics as well as a small status category.
 * Keep the result UI to this fixed, actionable copy; never expose a transport
 * exception, request details, or the raw failure category.
 */
export const usaSpendingRetryMessage = (run: SourceRun | undefined): string | null => {
  const status = String(run?.status ?? '').toLowerCase();
  if (!['unavailable', 'failed', 'rate_limited'].includes(status)) return null;
  if (String(run?.failure_category ?? '').toLowerCase() === 'timeout') {
    return 'USAspending did not respond in time. Select Calculate prices to try again.';
  }
  if (status === 'rate_limited') return 'USAspending is temporarily busy. Select Calculate prices to try again.';
  return 'USAspending is temporarily unavailable. Select Calculate prices to try again.';
};

/**
 * The ledger describes source families, while older revisions and fallback
 * lanes often use the result category as the label.  Keep that presentation
 * useful by naming the actual public source without exposing an adapter ID.
 */
export const sourceLedgerLabel = (label: string): string => {
  const normalized = label.trim().toLowerCase();
  if (/(^|[^a-z])usaspending([^a-z]|$)|previous federal awards|historical federal awards/.test(normalized)) {
    return 'Previous federal awards — USAspending';
  }
  if (/(^|[^a-z])btb([^a-z]|$)|national technology cost benchmark|national planning baseline|buildings technology baseline/.test(normalized)) {
    return 'National technology cost benchmark — BTB';
  }
  return label;
};

export const sourceLedgerForDisplay = (
  sources: UnifiedSourceCheckedPublic[],
): UnifiedSourceCheckedPublic[] => {
  const entries = new Map<string, UnifiedSourceCheckedPublic>();
  sources.forEach((source) => {
    const label = sourceLedgerLabel(source.label);
    const existing = entries.get(label);
    // Preserve the first server-curated detail. Duplicate legacy aliases map
    // to one human-facing source entry.
    if (!existing || (!existing.detail && source.detail)) entries.set(label, { ...source, label });
  });
  return Array.from(entries.values());
};

export const sourcesCheckedWithAwardFailure = (
  sources: UnifiedSourceCheckedPublic[],
  sourceRuns: SourceRun[],
): UnifiedSourceCheckedPublic[] => {
  const message = usaSpendingRetryMessage(usaspendingSourceRun(sourceRuns));
  if (!message) return sources;
  const entryIndex = sources.findIndex((source) => /previous federal awards|usaspending/i.test(source.label));
  if (entryIndex < 0) return [...sources, { label: 'Previous federal awards', detail: message }];
  return sources.map((source, index) => index === entryIndex ? { ...source, detail: message } : source);
};

export const ROM_SOURCE_LANE_STATES = new Set<UnifiedSourceLaneState>([
  'complete', 'reused', 'no_matching_evidence', 'unavailable', 'failed',
]);

/**
 * A legacy revision has no server-curated source ledger. Its result itself is
 * still public, so use only displayed lane/line attribution as a temporary
 * fallback. New unified results use `sources_checked` verbatim.
 */
export const fallbackRomSourcesChecked = (
  research: UnifiedSourceResearchPublic | null | undefined,
  plan: ApproximateCostPlanPublic,
  schedule: DavisBaconScheduleSummary | null,
): Array<{ label: string; detail?: string }> => {
  const entries = new Map<string, { label: string; detail?: string }>();
  const add = (label: unknown, detail?: unknown) => {
    if (typeof label !== 'string' || !label.trim() || label.length > 200) return;
    const trimmed = label.trim();
    if (entries.has(trimmed)) return;
    entries.set(trimmed, {
      label: trimmed,
      ...(typeof detail === 'string' && detail.trim() && detail.length <= 500 ? { detail: detail.trim() } : {}),
    });
  };
  research?.lanes.filter((lane) => ROM_SOURCE_LANE_STATES.has(lane.state)).forEach((lane) => add(lane.label, lane.message));
  plan.labor_lines.forEach((line) => add(line.source_label));
  plan.material_lines.forEach((line) => add(line.source_label));
  if (schedule?.source_name === 'SAM.gov') add('Wage determinations', 'SAM.gov determination used for labor rates.');
  return Array.from(entries.values());
};

export const canonicalAwardKey = (award: Record<string, any>): string => String(
  award.award_family_id ?? award.award_id ?? award.source_record_id ?? '',
);

export const canonicalAwardRows = (
  research: Record<string, any> | null,
  revision: EstimateRevision | null,
): Record<string, any>[] => {
  const researchItems = Array.isArray(research?.comparables?.items)
    ? research!.comparables.items as Record<string, any>[] : [];
  const revisionItems = Array.isArray(revision?.comparables?.items)
    ? revision!.comparables!.items as Record<string, any>[] : [];
  const revisionByKey = new Map(revisionItems.map((item) => [canonicalAwardKey(item), item]));
  const primary = researchItems.length > 0 ? researchItems : revisionItems;
  const seen = new Set<string>();
  return primary.reduce<Record<string, any>[]>((rows, item) => {
    const key = canonicalAwardKey(item);
    if (!key || seen.has(key)) return rows;
    seen.add(key);
    rows.push({ ...item, ...(revisionByKey.get(key) ?? {}) });
    return rows;
  }, []);
};

export function constructionResultResearch(
  load: EstimateLoadData | null,
  revision: EstimateRevision | null,
  job: EstimateJob | null,
) {
  const sourceStatuses = revision?.source_statuses ?? load?.source_statuses ?? [];
  const explicitSourceRuns = sourceRunRecords(revision?.source_runs);
  const researchSnapshot = (
    job?.research_snapshot
    ?? (load?.job as Record<string, any> | null)?.research_snapshot
    ?? load?.historical_award_research
  ) as Record<string, any> | null;
  const candidate = revision?.unified_source_research
    ?? job?.unified_source_research
    ?? (researchSnapshot?.unified_source_research as UnifiedSourceResearchPublic | undefined)
    ?? (researchSnapshot?.recommendation
      ? researchSnapshot as UnifiedSourceResearchPublic : undefined)
    ?? load?.unified_source_research;
  const normalized = typeof normalizeUnifiedSourceResearchPublic === 'function'
    ? normalizeUnifiedSourceResearchPublic(candidate) : undefined;
  const unifiedSourceResearch = normalized === undefined
    ? projectUnifiedResearch(candidate) : normalized;
  const candidatePublicFields = candidate && typeof candidate === 'object'
    && !Array.isArray(candidate) ? candidate : null;
  const approximateCostPlan = normalizeApproximateCostPlan(
    revision?.approximate_cost_plan ?? unifiedSourceResearch?.approximate_cost_plan
      ?? candidatePublicFields?.approximate_cost_plan,
  );
  const primaryApproximateCostPlan = statewideAwardReferencePlan(
    approximateCostPlan ?? unavailableApproximateCostPlan(unifiedSourceResearch, revision),
    unifiedSourceResearch,
  );
  const researchSourceRuns = sourceRunRecords(
    researchSnapshot?.source_runs ?? job?.source_runs
      ?? (load?.job as Record<string, any> | null)?.source_runs,
  );
  const sourceEvidenceRuns = researchSourceRuns.length > 0
    ? researchSourceRuns
    : explicitSourceRuns.length > 0
      ? explicitSourceRuns : sourceRunRecords(sourceStatuses);
  return {
    researchSnapshot, unifiedSourceResearch, primaryApproximateCostPlan,
    isolatedSourceLedger: projectSourceLedger(candidatePublicFields?.sources_checked),
    sourceEvidenceRuns, canonicalAwards: canonicalAwardRows(researchSnapshot, revision),
  };
}
